import React from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowRight,
  FiCheck,
  FiBriefcase,
  FiUsers,
  FiLayers,
  FiDatabase,
  FiZap,
  FiActivity,
  FiBarChart2,
  FiSettings,
  FiGitBranch,
  FiShield,
  FiMessageSquare,
  FiTarget,
  FiGrid,
  FiCpu,
  FiSearch,
  FiFileText,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./Enterprise.css";

const Enterprise = () => {
  return (
    <>
      <Helmet>
        <title>Enterprise AI Solutions | Dropgear IO</title>

        <meta
          name="description"
          content="Dropgear IO helps enterprises connect teams, automate workflows, organize knowledge, and build practical AI systems around their existing business operations."
        />
      </Helmet>

      <Header />

      <main className="enterprise-page">

        {/* =====================================================
            HERO
        ===================================================== */}
        <section className="enterprise-hero">

          <div className="enterprise-container enterprise-hero-grid">

            <div className="enterprise-hero-content">

              <span className="enterprise-eyebrow">
                AI FOR ENTERPRISES
              </span>

              <h1>
                Build an intelligent
                <span> operating layer for your business.</span>
              </h1>

              <p>
                Dropgear IO helps enterprises connect operational workflows,
                business knowledge, teams, and AI-powered automation into
                practical systems designed around the way work actually gets
                done.
              </p>

              <div className="enterprise-hero-actions">

                <a
                  href="/contact"
                  className="enterprise-btn-primary"
                >
                  Talk to Our Team
                  <FiArrowRight />
                </a>

                <a
                  href="#enterprise-capabilities"
                  className="enterprise-btn-secondary"
                >
                  Explore Capabilities
                </a>

              </div>

              <div className="enterprise-hero-points">

                <span>
                  <FiCheck />
                  Workflow automation
                </span>

                <span>
                  <FiCheck />
                  Connected knowledge
                </span>

                <span>
                  <FiCheck />
                  Human oversight
                </span>

              </div>

            </div>


            {/* ENTERPRISE DASHBOARD */}

            <div className="enterprise-dashboard-wrapper">

              <div className="enterprise-dashboard">

                <div className="enterprise-dashboard-header">

                  <div>

                    <span>Dropgear IO</span>

                    <strong>
                      Enterprise Operations
                    </strong>

                  </div>

                  <div className="enterprise-status">
                    <i></i>
                    Active
                  </div>

                </div>


                <div className="enterprise-dashboard-metrics">

                  <div className="enterprise-metric">

                    <span>
                      Active Workflows
                    </span>

                    <strong>
                      128
                    </strong>

                    <small>
                      <FiActivity />
                      Running
                    </small>

                  </div>


                  <div className="enterprise-metric">

                    <span>
                      Teams Connected
                    </span>

                    <strong>
                      34
                    </strong>

                    <small>
                      <FiUsers />
                      Across operations
                    </small>

                  </div>


                  <div className="enterprise-metric">

                    <span>
                      AI Tasks
                    </span>

                    <strong>
                      2.8K
                    </strong>

                    <small>
                      <FiZap />
                      This period
                    </small>

                  </div>

                </div>


                <div className="enterprise-workflow-card">

                  <div className="enterprise-card-heading">

                    <div>

                      <span>
                        Workflow Intelligence
                      </span>

                      <strong>
                        Operational Activity
                      </strong>

                    </div>

                    <FiBarChart2 />

                  </div>


                  <div className="enterprise-workflow-row">

                    <span className="enterprise-workflow-icon">
                      <FiDatabase />
                    </span>

                    <div>
                      <strong>
                        Knowledge Processing
                      </strong>

                      <small>
                        284 information items processed
                      </small>
                    </div>

                    <b>
                      Active
                    </b>

                  </div>


                  <div className="enterprise-workflow-row">

                    <span className="enterprise-workflow-icon">
                      <FiGitBranch />
                    </span>

                    <div>
                      <strong>
                        Approval Workflow
                      </strong>

                      <small>
                        16 requests waiting for review
                      </small>
                    </div>

                    <b>
                      Review
                    </b>

                  </div>


                  <div className="enterprise-workflow-row">

                    <span className="enterprise-workflow-icon">
                      <FiMessageSquare />
                    </span>

                    <div>
                      <strong>
                        Team Assistant
                      </strong>

                      <small>
                        74 requests handled
                      </small>
                    </div>

                    <b>
                      Ready
                    </b>

                  </div>

                </div>


                <div className="enterprise-activity">

                  <div>
                    <span>
                      <FiCheck />
                    </span>

                    <p>
                      Customer workflow completed
                      <small>
                        Just now
                      </small>
                    </p>
                  </div>


                  <div>
                    <span>
                      <FiCpu />
                    </span>

                    <p>
                      AI workflow started
                      <small>
                        2 minutes ago
                      </small>
                    </p>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}
        <section className="enterprise-intro">

          <div className="enterprise-container">

            <div className="enterprise-section-heading">

              <span className="enterprise-eyebrow">
                ENTERPRISE COMPLEXITY
              </span>

              <h2>
                Your business is connected.
                <span> Your workflows should be too.</span>
              </h2>

              <p>
                Large organizations operate across departments, systems,
                processes, and teams. Information often moves through multiple
                tools before reaching the person who needs it. Dropgear IO
                helps create intelligent connections between those operational
                layers.
              </p>

            </div>

          </div>

        </section>


        {/* =====================================================
            CAPABILITIES
        ===================================================== */}
        <section
          className="enterprise-capabilities"
          id="enterprise-capabilities"
        >

          <div className="enterprise-container">

            <div className="enterprise-section-heading centered">

              <span className="enterprise-eyebrow">
                ENTERPRISE CAPABILITIES
              </span>

              <h2>
                AI designed for
                <span> complex operations.</span>
              </h2>

              <p>
                Build practical AI capabilities across the workflows and
                information systems your organization already depends on.
              </p>

            </div>


            <div className="enterprise-capabilities-grid">

              {/* CARD 1 */}

              <div className="enterprise-capability-card">

                <div className="enterprise-card-icon">
                  <FiGitBranch />
                </div>

                <h3>
                  Workflow Automation
                </h3>

                <p>
                  Design intelligent workflows that coordinate repetitive
                  tasks, information movement, approvals, and operational
                  actions.
                </p>

                <ul>

                  <li>
                    <FiCheck />
                    Multi-step workflows
                  </li>

                  <li>
                    <FiCheck />
                    Approval routing
                  </li>

                  <li>
                    <FiCheck />
                    Automated actions
                  </li>

                </ul>

              </div>


              {/* CARD 2 */}

              <div className="enterprise-capability-card">

                <div className="enterprise-card-icon">
                  <FiDatabase />
                </div>

                <h3>
                  Enterprise Knowledge
                </h3>

                <p>
                  Make business information easier for teams and AI systems to
                  find, understand, and use.
                </p>

                <ul>

                  <li>
                    <FiCheck />
                    Knowledge retrieval
                  </li>

                  <li>
                    <FiFileText />
                    Document intelligence
                  </li>

                  <li>
                    <FiCheck />
                    Internal information access
                  </li>

                </ul>

              </div>


              {/* CARD 3 */}

              <div className="enterprise-capability-card">

                <div className="enterprise-card-icon">
                  <FiCpu />
                </div>

                <h3>
                  AI Assistants
                </h3>

                <p>
                  Give teams intelligent assistants that can support research,
                  information retrieval, summarization, and everyday work.
                </p>

                <ul>

                  <li>
                    <FiCheck />
                    Team assistants
                  </li>

                  <li>
                    <FiCheck />
                    Business Q&A
                  </li>

                  <li>
                    <FiCheck />
                    AI-supported tasks
                  </li>

                </ul>

              </div>


              {/* CARD 4 */}

              <div className="enterprise-capability-card">

                <div className="enterprise-card-icon">
                  <FiGrid />
                </div>

                <h3>
                  Cross-Team Operations
                </h3>

                <p>
                  Connect workflows across departments so information and
                  actions can move between teams more effectively.
                </p>

                <ul>

                  <li>
                    <FiCheck />
                    Department workflows
                  </li>

                  <li>
                    <FiCheck />
                    Shared processes
                  </li>

                  <li>
                    <FiCheck />
                    Operational coordination
                  </li>

                </ul>

              </div>


              {/* CARD 5 */}

              <div className="enterprise-capability-card">

                <div className="enterprise-card-icon">
                  <FiBarChart2 />
                </div>

                <h3>
                  Operational Intelligence
                </h3>

                <p>
                  Turn workflow activity into useful operational visibility for
                  teams and business leaders.
                </p>

                <ul>

                  <li>
                    <FiCheck />
                    Activity monitoring
                  </li>

                  <li>
                    <FiCheck />
                    Performance insights
                  </li>

                  <li>
                    <FiCheck />
                    Operational reporting
                  </li>

                </ul>

              </div>


              {/* CARD 6 */}

              <div className="enterprise-capability-card">

                <div className="enterprise-card-icon">
                  <FiSettings />
                </div>

                <h3>
                  Flexible AI Systems
                </h3>

                <p>
                  Start with focused workflows and expand AI capabilities as
                  your organization's needs evolve.
                </p>

                <ul>

                  <li>
                    <FiCheck />
                    Modular implementation
                  </li>

                  <li>
                    <FiCheck />
                    Flexible workflows
                  </li>

                  <li>
                    <FiCheck />
                    Continuous improvement
                  </li>

                </ul>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CONNECTED WORKSPACE
        ===================================================== */}
        <section className="enterprise-workspace">

          <div className="enterprise-container enterprise-workspace-grid">

            <div className="enterprise-workspace-visual">

              <div className="enterprise-workspace-window">

                <div className="enterprise-window-top">

                  <span>
                    Enterprise Workspace
                  </span>

                  <div>
                    <i></i>
                    <i></i>
                    <i></i>
                  </div>

                </div>


                <div className="enterprise-window-content">

                  <div className="enterprise-workspace-title">

                    <span>
                      Operations
                    </span>

                    <strong>
                      Connected Workflows
                    </strong>

                  </div>


                  <div className="enterprise-system-map">

                    <div className="enterprise-system-node">

                      <span>
                        <FiUsers />
                      </span>

                      <strong>
                        Teams
                      </strong>

                    </div>


                    <div className="enterprise-system-line"></div>


                    <div className="enterprise-system-node main">

                      <span>
                        <FiCpu />
                      </span>

                      <strong>
                        Dropgear AI
                      </strong>

                    </div>


                    <div className="enterprise-system-line"></div>


                    <div className="enterprise-system-node">

                      <span>
                        <FiDatabase />
                      </span>

                      <strong>
                        Knowledge
                      </strong>

                    </div>

                  </div>


                  <div className="enterprise-system-cards">

                    <div>

                      <FiBriefcase />

                      <p>
                        Operations
                        <small>
                          18 workflows
                        </small>
                      </p>

                    </div>


                    <div>

                      <FiMessageSquare />

                      <p>
                        Customer Teams
                        <small>
                          32 active requests
                        </small>
                      </p>

                    </div>


                    <div>

                      <FiBarChart2 />

                      <p>
                        Analytics
                        <small>
                          Updated live
                        </small>
                      </p>

                    </div>

                  </div>

                </div>

              </div>

            </div>


            <div className="enterprise-workspace-content">

              <span className="enterprise-eyebrow">
                CONNECTED INTELLIGENCE
              </span>

              <h2>
                One intelligent layer
                <span> across your operations.</span>
              </h2>

              <p>
                Dropgear IO can sit across different operational workflows,
                helping teams connect information, automate actions, and
                interact with business knowledge through practical AI
                experiences.
              </p>


              <div className="enterprise-feature-list">

                <div>

                  <span>
                    <FiLayers />
                  </span>

                  <div>

                    <h3>
                      Connect workflows
                    </h3>

                    <p>
                      Bring related processes together instead of treating
                      every department as an isolated system.
                    </p>

                  </div>

                </div>


                <div>

                  <span>
                    <FiDatabase />
                  </span>

                  <div>

                    <h3>
                      Connect knowledge
                    </h3>

                    <p>
                      Help teams access the information they need within their
                      operational context.
                    </p>

                  </div>

                </div>


                <div>

                  <span>
                    <FiUsers />
                  </span>

                  <div>

                    <h3>
                      Keep people involved
                    </h3>

                    <p>
                      Allow teams to review, approve, and guide important
                      workflow decisions.
                    </p>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            HOW ENTERPRISE AI WORKS
        ===================================================== */}
        <section className="enterprise-how">

          <div className="enterprise-container">

            <div className="enterprise-section-heading centered">

              <span className="enterprise-eyebrow">
                A PRACTICAL APPROACH
              </span>

              <h2>
                Start focused.
                <span> Expand intelligently.</span>
              </h2>

              <p>
                Enterprise AI does not need to begin with a massive
                transformation. Start with a meaningful workflow and build
                from there.
              </p>

            </div>


            <div className="enterprise-steps">

              <div className="enterprise-step">

                <span>
                  01
                </span>

                <div className="enterprise-step-icon">
                  <FiSearch />
                </div>

                <h3>
                  Identify
                </h3>

                <p>
                  Find high-value workflows where automation and intelligent
                  assistance can make a meaningful difference.
                </p>

              </div>


              <div className="enterprise-step">

                <span>
                  02
                </span>

                <div className="enterprise-step-icon">
                  <FiSettings />
                </div>

                <h3>
                  Design
                </h3>

                <p>
                  Map the people, information, decisions, and actions involved
                  in the workflow.
                </p>

              </div>


              <div className="enterprise-step">

                <span>
                  03
                </span>

                <div className="enterprise-step-icon">
                  <FiZap />
                </div>

                <h3>
                  Deploy
                </h3>

                <p>
                  Introduce AI into the selected workflow with appropriate
                  human review and operational controls.
                </p>

              </div>


              <div className="enterprise-step">

                <span>
                  04
                </span>

                <div className="enterprise-step-icon">
                  <FiActivity />
                </div>

                <h3>
                  Scale
                </h3>

                <p>
                  Expand successful patterns across additional teams,
                  processes, and operational areas.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            ENTERPRISE OPERATIONS
        ===================================================== */}
        <section className="enterprise-operations">

          <div className="enterprise-container enterprise-operations-grid">

            <div className="enterprise-operations-content">

              <span className="enterprise-eyebrow">
                DESIGNED FOR REAL OPERATIONS
              </span>

              <h2>
                Make AI part of the
                <span> work your teams already do.</span>
              </h2>

              <p>
                The goal is not to add another layer of complexity. Dropgear
                IO is designed to work around real business processes, helping
                organizations introduce AI where it can provide practical
                value.
              </p>


              <div className="enterprise-check-list">

                <div>
                  <FiCheck />
                  Automate repetitive operational tasks
                </div>

                <div>
                  <FiCheck />
                  Route work between people and AI
                </div>

                <div>
                  <FiCheck />
                  Surface relevant business information
                </div>

                <div>
                  <FiCheck />
                  Monitor workflow activity
                </div>

                <div>
                  <FiCheck />
                  Expand automation gradually
                </div>

              </div>

            </div>


            <div className="enterprise-operations-card">

              <div className="enterprise-operations-card-header">

                <div>

                  <small>
                    Workflow Overview
                  </small>

                  <strong>
                    Customer Operations
                  </strong>

                </div>

                <FiActivity />

              </div>


              <div className="enterprise-operation-item">

                <span>
                  01
                </span>

                <div>

                  <strong>
                    Request received
                  </strong>

                  <small>
                    Customer channel
                  </small>

                </div>

                <FiCheck />

              </div>


              <div className="enterprise-operation-item">

                <span>
                  02
                </span>

                <div>

                  <strong>
                    AI classification
                  </strong>

                  <small>
                    Intent identified
                  </small>

                </div>

                <FiCpu />

              </div>


              <div className="enterprise-operation-item">

                <span>
                  03
                </span>

                <div>

                  <strong>
                    Workflow routing
                  </strong>

                  <small>
                    Assigned to team
                  </small>

                </div>

                <FiGitBranch />

              </div>


              <div className="enterprise-operation-item">

                <span>
                  04
                </span>

                <div>

                  <strong>
                    Human review
                  </strong>

                  <small>
                    Approval required
                  </small>

                </div>

                <FiUsers />

              </div>


              <div className="enterprise-operation-item">

                <span>
                  05
                </span>

                <div>

                  <strong>
                    Action completed
                  </strong>

                  <small>
                    Workflow closed
                  </small>

                </div>

                <FiCheck />

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            PEOPLE + AI
        ===================================================== */}
        <section className="enterprise-human">

          <div className="enterprise-container enterprise-human-box">

            <div className="enterprise-human-icon">
              <FiUsers />
            </div>


            <div className="enterprise-human-content">

              <span className="enterprise-eyebrow">
                HUMAN + AI
              </span>

              <h2>
                Enterprise AI should
                <span> strengthen teams.</span>
              </h2>

              <p>
                AI can handle repetitive work, process information, and
                support operational decisions. People remain responsible for
                context, judgment, approvals, and outcomes.
              </p>


              <div className="enterprise-human-points">

                <span>
                  <FiCheck />
                  Human review where appropriate
                </span>

                <span>
                  <FiCheck />
                  Controlled workflow actions
                </span>

                <span>
                  <FiCheck />
                  Clear operational visibility
                </span>

                <span>
                  <FiCheck />
                  Flexible automation levels
                </span>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}
        <section className="enterprise-cta">

          <div className="enterprise-container">

            <div className="enterprise-cta-box">

              <div>

                <span className="enterprise-eyebrow">
                  BUILD WITH DROPGEAR IO
                </span>

                <h2>
                  Ready to make your
                  <span> enterprise more intelligent?</span>
                </h2>

                <p>
                  Explore how Dropgear IO can help your organization automate
                  workflows, connect knowledge, and introduce practical AI
                  across the business.
                </p>

              </div>


              <a
                href="/contact"
                className="enterprise-btn-primary"
              >
                Talk to Our Team
                <FiArrowRight />
              </a>

            </div>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default Enterprise;